import {
  faBriefcase,
  faClipboardList,
  faClock,
  faComment,
  faExchangeAlt,
  faPlusCircle,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import CardViewModal from 'components/modules/project-management/card-view/CardViewModal';
import { format, formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useTaskDetails } from 'providers/TaskDetailsProvider';
import { GET_JOB_QUERY, Job } from 'services/api/job';
import { AdminActivityItem } from 'services/api/notification';

export interface ActivityLogItemProps {
  item: AdminActivityItem;
  className?: string;
}

const getIcon = (entityType: string, activityType: string) => {
  // Prefer action icon when possible, fallback to entity icon
  switch (activityType) {
    case 'created':
      return <FontAwesomeIcon icon={faPlusCircle} className="text-success" />;
    case 'updated':
      return <FontAwesomeIcon icon={faExchangeAlt} className="text-warning" />;
    case 'deleted':
      return <FontAwesomeIcon icon={faTrash} className="text-danger" />;
    case 'comment':
      return <FontAwesomeIcon icon={faComment} className="text-info" />;
    default:
      return entityType === 'job' ? (
        <FontAwesomeIcon icon={faBriefcase} className="text-primary" />
      ) : (
        <FontAwesomeIcon icon={faClipboardList} className="text-primary" />
      );
  }
};

const ActivityLogItem = ({ item, className }: ActivityLogItemProps) => {
  const { openTask } = useTaskDetails();
  const [showJobModal, setShowJobModal] = useState(false);
  const [jobData, setJobData] = useState<Job | null>(null);

  const createdDate = new Date(item.createdAt);
  const timeAgo = formatDistanceToNow(createdDate, { addSuffix: true });
  const formattedDate = format(createdDate, 'MMM dd, yyyy');
  const formattedTime = format(createdDate, 'hh:mm a');

  useQuery(['job', item.job?.id], () => GET_JOB_QUERY(item.job?.id as string), {
    enabled: Boolean(item.job?.id) && showJobModal,
    onSuccess: response => {
      setJobData(response.data);
    }
  });

  const userName =
    item.user?.firstName || item.user?.lastName
      ? `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim()
      : item.user?.username || 'Someone';

  const title =
    item.entityType === 'job'
      ? `Job Activity${item.job?.jobNumber ? ` • ${item.job.jobNumber}` : ''}`
      : `Task Activity${item.task?.task ? ` • ${item.task.task}` : ''}`;

  const handleClick = () => {
    if (item.entityType === 'task' && item.task?.id) {
      openTask(item.task.id);
      return;
    }
    if (item.job?.id) {
      setShowJobModal(true);
    }
  };

  const isClickable =
    (item.entityType === 'task' && Boolean(item.task?.id)) ||
    Boolean(item.job?.id);

  return (
    <>
      <div
        className={classNames(
          className,
          'py-3 border-300 notification-card position-relative',
          'px-2 px-sm-3',
          { 'cursor-pointer': isClickable }
        )}
        onClick={isClickable ? handleClick : undefined}
      >
        <div className="d-flex align-items-center justify-content-between position-relative">
          <div className="d-flex">
            <div className="notification-icon d-flex align-items-center justify-content-center me-3 position-relative">
              <span className="fs-5">
                {getIcon(item.entityType, item.activityType)}
              </span>
            </div>
            <div className="flex-1 me-sm-3">
              <h4 className="fs-9 text-black">{title}</h4>
              <p className="fs-9 text-1000 mb-2 mb-sm-3">
                <span>
                  <span className="fw-bold">{userName}</span>
                  <span className="mx-1">•</span>
                  <span>{item.description}</span>
                </span>
                <span className="ms-2 text-600 fw-bold fs-10">{timeAgo}</span>
              </p>
              <p className="text-800 fs-9 mb-0">
                <FontAwesomeIcon icon={faClock} className="me-1" />
                <span className="fw-bold">{formattedTime}</span> {formattedDate}
              </p>
            </div>
          </div>
        </div>
      </div>

      {showJobModal && jobData && (
        <CardViewModal
          show={showJobModal}
          handleClose={() => {
            setShowJobModal(false);
            setJobData(null);
          }}
          project={jobData}
          canEditJob={false}
          canAddCommentsField={true}
          canAddShopCommentsField={false}
          canAddJobStageCommentsField={false}
          isLoadingTasks={false}
          jobTasks={[]}
          scrollToTasks={false}
          setScrollToTasks={undefined}
        />
      )}
    </>
  );
};

export default ActivityLogItem;
